import { applyPerspectiveWarp } from "./newPerspectiveWarp";
import { generateGliderolFilename } from "./fileNaming";

/**
 * Generates a high-resolution image with door overlay
 * @param {Object} params - Parameters for image generation
 * @param {string} params.bgImage - Background image URL
 * @param {string} params.doorImage - Door image URL  
 * @param {Array} params.points - Array of 4 points for door positioning
 * @param {Object} params.houseImageDimensions - Original house image dimensions {width, height}
 * @param {Object} params.containerDimensions - Container dimensions {width, height} (default: {width: 370, height: 300})
 * @param {number} params.targetWidth - Target export width (default: 1200)
 * @param {string} params.format - Export format 'jpeg' or 'png' (default: 'jpeg')
 * @param {number} params.quality - JPEG quality 0-1 (default: 0.95)
 * @returns {Promise<string>} - Data URL of the generated image
 */
export const generateHighResImage = async ({
  bgImage,
  doorImage,
  points,
  houseImageDimensions,
  containerDimensions = { width: 370, height: 300 },
  targetWidth = 1200,
  format = 'jpeg',
  quality = 0.95
}) => {
  if (!bgImage || !doorImage || !points || points.length !== 4) {
    throw new Error('Missing required parameters for image generation');
  }

  console.log('=== HIGH-RES IMAGE GENERATION ===');
  console.log('Points from dragging:', points);
  console.log('House image dimensions:', houseImageDimensions);
  console.log('Container dimensions:', containerDimensions);
  console.log('Target width:', targetWidth);

  // Load background image to get actual dimensions if needed
  const bgImg = new Image();
  bgImg.crossOrigin = "anonymous";
  bgImg.src = bgImage;
  
  await new Promise((resolve, reject) => {
    bgImg.onload = resolve;
    bgImg.onerror = reject;
  });

  // Use actual image dimensions instead of passed dimensions to ensure accuracy
  const actualDimensions = {
    width: bgImg.naturalWidth,
    height: bgImg.naturalHeight
  };
  
  console.log('Actual background image dimensions:', actualDimensions);

  // Calculate aspect ratio from actual dimensions
  const aspectRatio = actualDimensions.height / actualDimensions.width;
  console.log('Calculated aspect ratio:', aspectRatio);

  // FORCE exact dimensions - always use targetWidth regardless of input
  const exportWidth = 1200; // Hard-coded to 1200, no variables
  const exportHeight = Math.round(1200 * aspectRatio);
  
  console.log('HARD-CODED export dimensions:', exportWidth, 'x', exportHeight);
  console.log('aspectRatio used:', aspectRatio);
  
  // Calculate scale factors from container to FORCED export dimensions
  const scaleFactorX = 1200 / containerDimensions.width;  // Hard-coded 1200
  const scaleFactorY = exportHeight / containerDimensions.height;

  console.log('HARD-CODED scale factors - X:', scaleFactorX, 'Y:', scaleFactorY);

  // Create canvas with FORCED dimensions
  const canvas = document.createElement("canvas");
  
  // Set dimensions multiple times to ensure they stick
  canvas.width = 1200;  // Hard-coded
  canvas.height = exportHeight;
  
  console.log('Canvas IMMEDIATELY after setting:', canvas.width, 'x', canvas.height);
  
  // Force set again
  canvas.setAttribute('width', '1200');
  canvas.setAttribute('height', exportHeight.toString());
  
  console.log('Canvas after setAttribute:', canvas.width, 'x', canvas.height);
  
  const ctx = canvas.getContext("2d");
  
  console.log('Canvas after getContext:', canvas.width, 'x', canvas.height);

  // Enable high-quality rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  console.log('Canvas after setting context properties:', canvas.width, 'x', canvas.height);

  // Load images
  const door = new Image();
  door.crossOrigin = "anonymous";
  door.src = doorImage;

  // Wait for door image to load (background already loaded above)
  await new Promise((resolve, reject) => {
    door.onload = resolve;
    door.onerror = reject;
  });

  // Draw background at FORCED export resolution
  ctx.drawImage(bgImg, 0, 0, 1200, exportHeight);  // Hard-coded 1200
  
  console.log('Canvas after drawImage:', canvas.width, 'x', canvas.height);

  // Scale points proportionally for export using hard-coded 1200
  const scaledPoints = points.map(point => ({
    x: point.x * (1200 / containerDimensions.width),    // Hard-coded 1200
    y: point.y * (exportHeight / containerDimensions.height)
  }));

  console.log('Scaled points for export:', scaledPoints);
  
  console.log('Canvas before applyPerspectiveWarp:', canvas.width, 'x', canvas.height);

  // Apply perspective warp with anti-aliasing
  applyPerspectiveWarp({
    ctx,
    img: door,
    from: [
      { x: 0, y: 0 },
      { x: door.naturalWidth, y: 0 },
      { x: door.naturalWidth, y: door.naturalHeight },
      { x: 0, y: door.naturalHeight },
    ],
    to: scaledPoints,
  });

  console.log('Canvas after applyPerspectiveWarp:', canvas.width, 'x', canvas.height);

  // Convert to data URL
  const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
  const dataUrl = canvas.toDataURL(mimeType, quality);

  console.log('Canvas before dataURL conversion:', canvas.width, 'x', canvas.height);
  console.log(`High-res export complete: ${1200}x${exportHeight}px`);
  console.log('Canvas final size:', canvas.width, 'x', canvas.height);
  
  // Double-check by creating a test image to verify dimensions
  const testImg = new Image();
  testImg.src = dataUrl;
  await new Promise(resolve => {
    testImg.onload = () => {
      console.log('🔍 FINAL VERIFICATION - Image natural dimensions:', testImg.naturalWidth, 'x', testImg.naturalHeight);
      if (testImg.naturalWidth !== 1200) {
        console.error('🚨 FINAL IMAGE IS NOT 1200px! Actual width:', testImg.naturalWidth);
        console.error('🚨 Something is resizing our canvas! Canvas was:', canvas.width);
      } else {
        console.log('✅ Final image is correctly 1200px wide');
      }
      resolve();
    };
  });
  
  return dataUrl;
};

/**
 * Downloads an image from a data URL
 * @param {string} dataUrl - Data URL of the image
 * @param {string} filename - Filename for download (default: generated)
 */
export const downloadImage = (dataUrl, filename) => {
  const defaultFilename = generateGliderolFilename();
  const link = document.createElement("a");
  link.download = filename || defaultFilename;
  link.href = dataUrl;
  link.click();
};

/**
 * Converts a data URL to a Blob
 * @param {string} dataUrl - Data URL to convert
 * @returns {Promise<Blob>} - Blob object
 */
export const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return await response.blob();
};

/**
 * Converts a blob to base64 string
 * @param {Blob} blob - Blob to convert
 * @returns {Promise<string>} - Base64 string (without data: prefix)
 */
export const blobToBase64 = async (blob) => {
  const arrayBuffer = await blob.arrayBuffer();
  const u8arr = new Uint8Array(arrayBuffer);
  
  let binary = "";
  for (let i = 0; i < u8arr.byteLength; i++) {
    binary += String.fromCharCode(u8arr[i]);
  }
  
  return btoa(binary);
};